import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, Building2, User, Bike, LayoutGrid } from "lucide-react";
import api from "../../lib/api";
import useDebouncedValue from "../../lib/useDebouncedValue";

const ROLE_LABELS = {
    b2b: "Händler", b2c: "Kunde", admin: "Admin", vermittler: "Vermittler",
    zweiradverkaeufer: "Mitarbeiter", service_provider: "Dienstleister",
};

// Jul-2026 (User-Wunsch) — alle Admin-Bereiche durchsuchbar: Label + Stichwörter.
const ADMIN_AREAS = [
    { label: "Admin-Übersicht", to: "/admin", kw: "dashboard übersicht at a glance" },
    { label: "Eingehende Leads", to: "/admin/leads", kw: "leads autobörse anfragen kaufanfragen" },
    { label: "Interessensanfragen (Teaser)", to: "/admin/contact-leads", kw: "teaser interesse kontakt demo" },
    { label: "Händler-Verwaltung", to: "/admin/dealers", kw: "händler haendler verifizierung freigabe b2b" },
    { label: "Konto-Wechsel", to: "/admin/impersonation", kw: "impersonation als nutzer anmelden konto wechsel" },
    { label: "Konten-Werkzeuge / Einstellungen", to: "/admin/konten", kw: "einstellungen zahnrad konto werkzeuge passwort" },
    { label: "Betreiberdaten (Impressum & E-Mail-Signatur)", to: "/admin/betreiber", kw: "betreiber impressum firmendaten signatur inhaber ust" },
    { label: "E-Mail-Center (Postfächer)", to: "/dealer/email", kw: "email e-mail postfach imap smtp verbinden aktivieren" },
    { label: "E-Mail-Gesundheit", to: "/admin/email-health", kw: "email bounce zustellung gesundheit" },
    { label: "Newsletter", to: "/admin/newsletter", kw: "newsletter kampagne digest versand" },
    { label: "Compliance-Übersicht", to: "/admin/compliance", kw: "compliance impressum recht prüfung" },
    { label: "Compliance-Historie", to: "/admin/compliance/history", kw: "compliance historie verlauf" },
    { label: "Spam-Prüfung", to: "/admin/compliance/spam", kw: "spam prüfung compliance" },
    { label: "Vermittler", to: "/admin/vermittlers", kw: "vermittler freischaltung provision" },
    { label: "Empfehlungs-Leads", to: "/admin/vermittler-referrals", kw: "empfehlung referral vermittler" },
    { label: "Dienstleister", to: "/admin/service-providers", kw: "dienstleister anträge werkstatt partner" },
    { label: "Bewertungs-Moderation", to: "/admin/reviews", kw: "bewertungen moderation sterne reviews" },
    { label: "€Punkte-Verwaltung", to: "/admin/points", kw: "punkte points bonus" },
    { label: "Wallet-Verwaltung", to: "/admin/wallet", kw: "wallet guthaben" },
    { label: "Pfand & Refinanzierung", to: "/admin/pawn", kw: "pfand refinanzierung pawn" },
    { label: "Tarife & Pakete", to: "/admin/tariffs", kw: "tarife pakete preise abo" },
    { label: "SEPA-Abrechnung (Kosten)", to: "/admin/usage-billing", kw: "sepa abrechnung kosten lastschrift billing" },
    { label: "PStTG / DAC7-Meldung", to: "/admin/psttg", kw: "psttg dac7 steuer meldung finanzamt" },
    { label: "Gemeldete Angebote (DSA)", to: "/admin/listing-reports", kw: "dsa meldungen gemeldet angebote" },
    { label: "Top-Inserat-Tarif", to: "/admin/top-listing", kw: "top inserat tarif hervorheben" },
    { label: "SMS-Verlauf", to: "/admin/sms/history", kw: "sms verlauf twilio nachrichten" },
    { label: "Support-Tickets", to: "/admin/tickets", kw: "support tickets hilfe anfragen" },
    { label: "Assistenz-Nutzung", to: "/admin/assistant-stats", kw: "ki assistent statistik nutzung kosten" },
    { label: "Hilfe-Übersicht", to: "/admin/hilfe-uebersicht", kw: "hilfe übersicht faq" },
    { label: "Fahrzeug-Kategorien", to: "/admin/system/categories", kw: "kategorien fahrzeug system" },
    { label: "Kunden-Doubletten", to: "/admin/customer-duplicates", kw: "dubletten doubletten kunden crm datenqualität" },
    { label: "Fahrzeug-Doubletten", to: "/admin/vehicle-duplicates", kw: "dubletten doubletten fahrzeuge vin datenqualität" },
    { label: "Cron-Status", to: "/admin/cron-status", kw: "cron jobs hintergrund status" },
    { label: "Health-Monitor", to: "/admin/health-monitor", kw: "health monitor status system" },
    { label: "KBA-Datenbank (gelernt)", to: "/admin/hsn-tsn", kw: "kba hsn tsn schlüsselnummer" },
    { label: "Marktbeobachtung", to: "/admin/market-watch", kw: "markt mitbewerber beobachtung preise" },
    { label: "Portal-Zustimmungen", to: "/admin/portal-consent", kw: "consent zustimmung portal" },
    { label: "DATEV-Auto-Versand", to: "/admin/datev/auto-send", kw: "datev buchhaltung steuerberater export" },
    { label: "GoBD-Archiv", to: "/admin/gobd", kw: "gobd archiv aufbewahrung" },
    { label: "Doku / Handbuch", to: "/admin/doku", kw: "doku dokumentation handbuch anleitung" },
];

function matchAreas(q) {
    const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return ADMIN_AREAS.filter((a) => {
        const hay = `${a.label} ${a.kw}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
    }).slice(0, 6);
}

// Jul-2026 (User-Wunsch) — Universalsuche im Admin At-a-Glance: findet Konten
// (alle Rollen) und Inserate in einem Feld; Klick springt in den passenden Bereich.
export const AdminUniversalSearch = () => {
    const [q, setQ] = useState("");
    const dq = useDebouncedValue(q, 350);
    const [users, setUsers] = useState([]);
    const [listings, setListings] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!dq || dq.trim().length < 2) { setUsers([]); setListings([]); return undefined; }
        let cancel = false;
        (async () => {
            setBusy(true);
            const [u, l] = await Promise.all([
                api.get("/admin/users/search", { params: { q: dq, limit: 8 } }).catch(() => ({ data: { items: [] } })),
                api.get("/admin/listings", { params: { q: dq, limit: 8 } }).catch(() => ({ data: [] })),
            ]);
            if (cancel) return;
            setUsers(u.data.items || []);
            setListings(Array.isArray(l.data) ? l.data : []);
            setBusy(false);
        })();
        return () => { cancel = true; };
    }, [dq]);

    const userTarget = (u) => (u.role === "b2b"
        ? `/admin/dealers?q=${encodeURIComponent(u.email || "")}`
        : `/admin/impersonation?q=${encodeURIComponent(u.email || "")}`);
    const showResults = q.trim().length >= 2;
    const areas = showResults ? matchAreas(q.trim()) : [];

    return (
        <div className="mb-8" data-testid="admin-universal-search">
            <div className="relative max-w-2xl">
                {busy
                    ? <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                    : <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />}
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Universalsuche: Bereiche, Händler, Nutzer, E-Mail, Inserat …"
                    className="w-full h-11 rounded-sm border-2 border-gray-300 pl-9 pr-3 text-sm font-medium focus:outline-none focus:border-[#111827]"
                    data-testid="admin-universal-search-input"
                />
            </div>
            {showResults && (areas.length > 0 || users.length > 0 || listings.length > 0 || !busy) && (
                <div className="mt-2 max-w-2xl rounded-sm border border-gray-200 bg-white shadow-sm divide-y divide-gray-100" data-testid="admin-universal-search-results">
                    {areas.length === 0 && users.length === 0 && listings.length === 0 && !busy && (
                        <div className="px-3 py-2.5 text-sm text-gray-500">Keine Treffer für „{q}“.</div>
                    )}
                    {areas.map((a, i) => (
                        <Link key={a.to} to={a.to} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors" data-testid={`admin-search-result-area-${i}`}>
                            <LayoutGrid className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-[#111827] truncate">{a.label}</div>
                                <div className="text-xs text-gray-500 truncate">{a.to}</div>
                            </div>
                            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] rounded-sm px-1.5 py-0.5">Bereich</span>
                        </Link>
                    ))}
                    {users.map((u, i) => (
                        <Link key={u.id} to={userTarget(u)} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors" data-testid={`admin-search-result-user-${i}`}>
                            {u.role === "b2b" ? <Building2 className="w-4 h-4 text-gray-400 shrink-0" /> : <User className="w-4 h-4 text-gray-400 shrink-0" />}
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-[#111827] truncate">{u.company_name || u.name || u.email}</div>
                                <div className="text-xs text-gray-500 truncate">{u.email}{u.city ? ` · ${u.city}` : ""}</div>
                            </div>
                            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 rounded-sm px-1.5 py-0.5">{ROLE_LABELS[u.role] || u.role}</span>
                        </Link>
                    ))}
                    {listings.map((l, i) => (
                        <Link key={l.id} to={`/listing/${l.id}`} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors" data-testid={`admin-search-result-listing-${i}`}>
                            <Bike className="w-4 h-4 text-gray-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-bold text-[#111827] truncate">{l.title}</div>
                                <div className="text-xs text-gray-500">{l.price ? `${Number(l.price).toLocaleString("de-DE")} €` : "—"}{l.sold ? " · verkauft" : ""}</div>
                            </div>
                            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 rounded-sm px-1.5 py-0.5">Inserat</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};
